import React, { useState } from 'react';
import { Tag, Icon, Tooltip, Intent, Card } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import './AttachmentPreview.css';

interface AttachmentPreviewProps {
  attachment: {
    id: string;
    fileName: string;
    fileSize?: number;
    mimeType?: string;
    url?: string;
    isImage?: boolean;
  };
  onRemove?: (id: string) => void;
  showRemove?: boolean;
}

/**
 * Attachment preview with image tooltip on hover
 */
export const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachment,
  onRemove,
  showRemove = true
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = () => {
    if (attachment.isImage) return IconNames.MEDIA;
    if (attachment.mimeType?.includes('pdf')) return IconNames.DOCUMENT;
    if (attachment.mimeType?.includes('text')) return IconNames.DOCUMENT_OPEN;
    if (attachment.mimeType?.includes('zip') || attachment.mimeType?.includes('archive')) return IconNames.COMPRESSED;
    return IconNames.PAPERCLIP;
  };

  const getFileIntent = () => {
    if (attachment.isImage) return Intent.SUCCESS;
    if (attachment.mimeType?.includes('pdf')) return Intent.DANGER;
    return Intent.NONE;
  };

  // Tooltip content with image preview for images
  const tooltipContent = attachment.isImage && attachment.url && !imageError ? (
    <Card className="attachment-preview-tooltip">
      <div className="preview-header">
        <strong>{attachment.fileName}</strong>
        {attachment.fileSize && (
          <span className="file-size">{formatFileSize(attachment.fileSize)}</span>
        )}
      </div>
      <div className="preview-image-container">
        <img
          src={attachment.url}
          alt={attachment.fileName}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          className={`preview-image ${imageLoaded ? 'loaded' : 'loading'}`}
        />
        {!imageLoaded && !imageError && (
          <div className="preview-loading">Loading preview...</div>
        )}
      </div>
    </Card>
  ) : (
    <div>
      <div><strong>{attachment.fileName}</strong></div>
      {attachment.fileSize && <div className="bp5-text-muted">{formatFileSize(attachment.fileSize)}</div>}
      {attachment.mimeType && <div className="bp5-text-small">{attachment.mimeType}</div>}
    </div>
  );

  return (
    <Tooltip
      content={tooltipContent}
      position="top"
      hoverOpenDelay={200}
      className="attachment-tooltip"
    >
      <Tag
        icon={getFileIcon()}
        intent={getFileIntent()}
        onRemove={showRemove && onRemove ? () => onRemove(attachment.id) : undefined}
        interactive={true}
        round={false}
        minimal={false}
      >
        <span className="attachment-name">{attachment.fileName}</span>
        {attachment.fileSize && (
          <span className="attachment-size"> ({formatFileSize(attachment.fileSize)})</span>
        )}
      </Tag>
    </Tooltip>
  );
};

